<template>
  <div
    v-if="editor"
    class="relative flex flex-col rounded-md border border-slate-300 shadow-sm"
    :class="{
      'pointer-events-none': disabled,
      'mt-2': label
    }"
    @click="focusEditor"
  >
    <BaseLabelTop
      v-if="label"
      :label="label"
      :target="name"
      :required="required"
      :disabled="disabled"
    />
    <div
      class="flex flex-1 flex-col"
      :class="{
        'bg-gray-100': disabled
      }"
    >
      <div class="flex flex-row">
        <div
          v-if="editionMode"
          class="flex flex-1 shrink-0 flex-row gap-2 p-2 pb-0"
        >
          <button
            class="rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('bold') }"
            @click.stop="editor.chain().focus().toggleBold().run()"
          >
            <FontAwesomeIcon :icon="['far', 'bold']" size="sm" />
          </button>
          <button
            class="rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('italic') }"
            @click.stop="editor.chain().focus().toggleItalic().run()"
          >
            <FontAwesomeIcon :icon="['far', 'italic']" size="sm" />
          </button>
          <button
            class="rounded border border-gray-300 px-2 py-1 leading-none"
            :class="{ 'bg-gray-200': editor.isActive('underline') }"
            @click.stop="editor.chain().focus().toggleUnderline().run()"
          >
            <FontAwesomeIcon :icon="['far', 'underline']" size="sm" />
          </button>
        </div>
        <div class="shrink-0 p-2 pb-0">
          <OpenAIPopover
            :model-notification="message"
            @update-message="updateMessageContent"
          />
          <MessageTextsPopover
            :model-id="modelId"
            :model-type="modelType"
            @update-message="updateMessageContent"
          />
        </div>
      </div>
    </div>
    <div v-if="editionMode" class="relative w-full flex-1 overflow-y-auto">
      <EditorContent
        v-if="editionMode"
        :editor="editor"
        class="min-h-[10rem] max-w-none p-3"
      />
<!--      <div v-if="signature" class="px-2 pb-2">-->
<!--        <div v-html="signature" class="bg-primary-100 rounded p-2"></div>-->
<!--      </div>-->
      <div
        v-if="signature"
        class="border-t pt-2"
        contenteditable="false"
      >
        <div
          class="[&_p]:!my-0 [&_p]:!leading-normal"
          v-html="signature"
        ></div>
      </div>

      <div class="absolute right-2 bottom-2">
        <SignaturePopover
          :model-id="modelId"
          :model-type="modelType"
          :from="from"
          :loading="signatureLoading"
          @update-signature="updateSignature"
        />
      </div>
    </div>
    <div
      v-else
      class="max-w-full flex-1 bg-gray-100 p-3"
      v-html="message"
    ></div>
  </div>
  <ErrorContainer :error-key="name" :errors="errors" />
</template>

<script>
import { mapActions } from 'vuex'
import { Editor, EditorContent } from '@tiptap/vue-3'
import { DOMParser } from 'prosemirror-model'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseLabelTop from '@c/addf-package/components/BaseLabel/BaseLabelTop.vue'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import OpenAIPopover from '@c/Mail/component/OpenAIPopover.vue'
import MessageTextsPopover from '@c/Mail/component/MessageTextsPopover.vue'
import SignaturePopover from '@c/Mail/component/SignaturePopover.vue'

export default {
  name: 'BaseWysiwygMail',
  components: {
    SignaturePopover,
    MessageTextsPopover,
    OpenAIPopover,
    EditorContent,
    FontAwesomeIcon,
    BaseLabelTop,
    ErrorContainer
  },
  props: {
    message: {
      type: String,
      required: true
    },
    signature: {
      type: String,
      default: ''
    },
    signatureLoading: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    editionMode: {
      type: Boolean,
      default: true
    },
    errors: {
      type: Object,
      required: false,
      default: () => {}
    },
    modelType: {
      type: String,
      required: false,
      default: null
    },
    modelId: {
      type: String,
      required: false,
      default: null
    },
    from: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['update:message', 'update:signature', 'change', 'dimensions:error'],
  data() {
    return {
      editor: null,
      initialized: false
    }
  },
  watch: {
    editionMode(val) {
      val ? this.initEditor() : this.editor?.destroy()
    },
    message(newValue, oldValue) {
      if (!this.editor) return

      const isEmpty = (value) =>
        !value || value === '<p></p>' || value === '<p><br></p>'

      // Load externally-provided content (fetch / template) while the editor
      // still holds nothing. The editor's own empty paragraph counts as empty,
      // so a message arriving after init still loads on first open.
      if (newValue !== oldValue && isEmpty(oldValue) && !isEmpty(newValue)) {
        this.insertHTMLContent(newValue)
      }
    }
  },
  mounted() {
    if (this.editionMode) this.initEditor()
  },
  beforeUnmount() {
    this.editor?.destroy()
  },
  methods: {
    ...mapActions({
      saveWysiwygImage: 'user/saveWysiwygImage'
    }),
    focusEditor() {
      if (!this.editor || this.disabled || !this.editionMode) return
      this.editor.chain().focus().run()
    },
    initEditor() {
      if (this.editor) return

      this.editor = new Editor({
        content: '', // on initialise vide
        editorProps: {
          attributes: { class: 'focus:outline-none' }
        },
        extensions: [
          StarterKit.configure({
            heading: false,
            bulletList: false,
            orderedList: false
          }),
          Document,
          Paragraph,
          Text,
          Underline,
          // Signature
        ],
        onUpdate: () => {
          const html = this.editor.getHTML()
          this.$emit('update:message', html)
        }
      })

      if (this.message) {
        this.insertHTMLContent(this.message)
      }

      this.initialized = true
    },

    /**
     * Injecte du HTML dans l'éditeur, en conservant la signature existante
     */
    updateMessageContent(payload) {

      // if (!this.editor || !html) return
      //
      // this.editor.commands.setContent(html, false);
      //
      // this.$emit('update:message', html);
      const html = payload.text
      if (!this.editor || !html) return

      this.editor.commands.setContent(html, false);

      this.$emit('update:message', payload);
    },

    /**
     * Met à jour la signature uniquement
     */
    updateSignature(html) {
      // this.signature = html
      this.$emit('update:signature', html)
    },

    /**
     * Méthode utilitaire pour injecter du HTML (contenu initial ou mis à jour)
     */
    insertHTMLContent(html) {
      const { editor } = this
      if (!editor || !html) return

      const wrapper = document.createElement('div')
      wrapper.innerHTML = html
      const parser = DOMParser.fromSchema(editor.schema)
      const parsedDoc = parser.parse(wrapper)

      // Remplace tout le contenu
      editor
        .chain()
        .command(({ tr, state }) => {
          const { doc } = state
          tr.replaceWith(0, doc.content.size, parsedDoc.content)
          return true
        })
        .run()
    }
  }
}
</script>
